class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onMobileFacetChange = this.onMobileFacetChange.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    facetForm.addEventListener('change', this.onMobileFacetChange);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static async renderPage(searchParams, event, updateURLHash = true, scroll = false) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return false;
    window.TimeShopCollectionPagination.cancelScroll();
    window.TimeShopCollectionPagination.cancelPrefetch();
    FacetFiltersForm.controller?.abort();
    const controller = new AbortController();
    FacetFiltersForm.controller = controller;
    const previousParams = FacetFiltersForm.searchParamsPrev;
    FacetFiltersForm.searchParamsPrev = searchParams;
    const url = new URL(window.location.pathname, window.location.origin);
    url.search = searchParams;
    url.searchParams.set('section_id', FacetFiltersForm.getSections()[0].section);
    container.querySelector('[data-ts-pagination-error]')?.remove();
    container.setAttribute('aria-busy', 'true');
    // Cached pages paint without a loading flash; slow requests retain the current cards.
    const loading = setTimeout(() => {
      if (controller.signal.aborted) return;
      document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
        .forEach((spinner) => spinner.classList.remove('hidden'));
    }, 180);
    const timeout = setTimeout(() => controller.abort(new Error('Collection request timed out')), 10000);
    try {
      const html = await window.TimeShopCollectionPagination.load(url, controller.signal);
      if (controller.signal.aborted || FacetFiltersForm.controller !== controller) return false;
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderBrandSeriesStrip(html);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
      document.dispatchEvent(new CustomEvent('timeshop:collection-grid-updated'));
      if (scroll) window.TimeShopCollectionPagination.scrollToResultsStart(controller.signal);
      window.TimeShopCollectionPagination.prefetchNext();
      return true;
    } catch (error) {
      if (FacetFiltersForm.controller !== controller) return false;
      if (controller.signal.aborted && controller.signal.reason?.name === 'AbortError') return false;
      FacetFiltersForm.searchParamsPrev = previousParams;
      const notice = document.createElement('p');
      notice.dataset.tsPaginationError = '';
      notice.setAttribute('role', 'alert');
      notice.className = 'page-width';
      notice.append('Страницата не успя да се зареди. ');
      const retry = document.createElement('a');
      url.searchParams.delete('section_id');
      retry.href = url.pathname + url.search;
      retry.className = 'underlined-link';
      retry.textContent = 'Опитай отново';
      notice.append(retry);
      container.prepend(notice);
      console.warn('TimeShop collection request failed:', error);
      return false;
    } finally {
      clearTimeout(loading);
      clearTimeout(timeout);
      if (FacetFiltersForm.controller === controller) {
        container.removeAttribute('aria-busy');
        container.querySelector('.collection')?.classList.remove('loading');
        document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
          .forEach((spinner) => spinner.classList.add('hidden'));
        document.querySelectorAll('[data-ts-brand-series]').forEach((strip) => {
          strip.removeAttribute('aria-busy');
          strip.classList.remove('is-loading');
        });
        FacetFiltersForm.toggleActiveFacets(false);
      }
    }
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    document.dispatchEvent(new CustomEvent('timeshop:product-grid-rendered'));
  }

  static renderBrandSeriesStrip(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const currentStrip = document.querySelector('[data-ts-brand-series]');
    const nextStrip = parsedHTML.querySelector('[data-ts-brand-series]');

    if (currentStrip && nextStrip) {
      currentStrip.replaceWith(nextStrip);
      TimeShopBrandSeries.init(nextStrip);
    } else if (currentStrip && !nextStrip) {
      currentStrip.remove();
    }
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onMobileFacetChange(event) {
    const checkbox = event.target.closest('input[type="checkbox"]');
    const details = checkbox?.closest('.mobile-facets__details');
    const facetsMain = details?.closest('.mobile-facets__main');
    const closeButton = details?.querySelector('.mobile-facets__close-button');

    if (!checkbox || !details?.open || !facetsMain || !closeButton) return;

    closeButton.click();
    window.requestAnimationFrame(() => {
      facetsMain.scrollTo({
        top: 0,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    });
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

const TimeShopBrandSeries = {
  imageCache: new Map(),
  loadingImages: new Map(),
  resizeBound: false,
  scrollFrame: null,
  mobileScrollMedia: window.matchMedia('(max-width: 749px)'),

  normalize(value) {
    return (value || '').trim().toLocaleLowerCase();
  },

  getProductSeries(rawValue) {
    if (!rawValue) return [];

    const trimmedValue = rawValue.trim();
    if (trimmedValue.startsWith('[')) {
      try {
        const parsedValue = JSON.parse(trimmedValue);
        if (Array.isArray(parsedValue)) return parsedValue.map((value) => String(value));
      } catch (error) {
        // Fall back to the plain text value below.
      }
    }

    return trimmedValue.split('||').map((value) => value.trim()).filter(Boolean);
  },

  getVisibleProductImages() {
    const productImages = new Map();

    document.querySelectorAll('#product-grid [data-ts-watch-collection]').forEach((card) => {
      const image = card.querySelector('.card__media img');
      if (!image) return;

      this.getProductSeries(card.dataset.tsWatchCollection).forEach((seriesName) => {
        const normalizedName = this.normalize(seriesName);
        if (!normalizedName || productImages.has(normalizedName)) return;

        productImages.set(normalizedName, {
          src: image.currentSrc || image.src,
          srcset: image.srcset || '',
        });
      });
    });

    return productImages;
  },

  setImage(link, imageData) {
    const image = link.querySelector('[data-ts-series-image]');
    const placeholder = link.querySelector('[data-ts-series-placeholder]');
    if (!image || !imageData?.src) return false;

    image.src = imageData.src;
    if (imageData.srcset) image.srcset = imageData.srcset;
    image.sizes = '64px';
    image.hidden = false;
    if (placeholder) placeholder.hidden = true;
    link.classList.add('has-image');
    return true;
  },

  async fetchRepresentativeImage(strip, link) {
    const cacheKey = `${window.location.pathname}::${this.normalize(link.dataset.seriesLabel)}`;
    if (this.imageCache.has(cacheKey)) return this.imageCache.get(cacheKey);
    if (this.loadingImages.has(cacheKey)) return this.loadingImages.get(cacheKey);

    const imagePromise = (async () => {
      const requestUrl = new URL(window.location.pathname, window.location.origin);
      requestUrl.searchParams.set('section_id', strip.dataset.sectionId);
      requestUrl.searchParams.set(link.dataset.seriesParam, link.dataset.seriesValue);

      try {
        const response = await fetch(requestUrl.toString(), { credentials: 'same-origin' });
        if (!response.ok) return null;

        const responseHTML = await response.text();
        const parsedHTML = new DOMParser().parseFromString(responseHTML, 'text/html');
        const image = parsedHTML.querySelector('#product-grid [data-product-card-item] .card__media img');
        if (!image?.src) return null;

        return {
          src: image.src,
          srcset: image.srcset || '',
        };
      } catch (error) {
        return null;
      }
    })();

    this.loadingImages.set(cacheKey, imagePromise);
    const imageData = await imagePromise;
    this.loadingImages.delete(cacheKey);
    if (imageData) this.imageCache.set(cacheKey, imageData);
    return imageData;
  },

  async hydrateImages(strip) {
    if (!strip?.isConnected) return;

    const visibleProductImages = this.getVisibleProductImages();
    const unresolvedLinks = [];

    strip.querySelectorAll('[data-ts-series-link]').forEach((link) => {
      if (!link.querySelector('[data-ts-series-image]') || link.classList.contains('has-image')) return;

      const visibleImage = visibleProductImages.get(this.normalize(link.dataset.seriesLabel));
      if (visibleImage && this.setImage(link, visibleImage)) return;

      unresolvedLinks.push(link);
    });

    let nextIndex = 0;
    const loadNextImage = async () => {
      while (nextIndex < unresolvedLinks.length) {
        const link = unresolvedLinks[nextIndex++];
        if (!link.isConnected) continue;

        const imageData = await this.fetchRepresentativeImage(strip, link);
        if (link.isConnected && imageData) this.setImage(link, imageData);
      }
    };

    await Promise.all(Array.from({ length: Math.min(3, unresolvedLinks.length) }, loadNextImage));
  },

  updateScrollControls(strip) {
    const rail = strip?.querySelector('[data-ts-series-rail]');
    if (!rail) return;

    const previousButton = strip.querySelector('[data-ts-series-scroll="previous"]');
    const nextButton = strip.querySelector('[data-ts-series-scroll="next"]');
    const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const isScrollable = maxScrollLeft > 2;

    strip.classList.toggle('is-scrollable', isScrollable);
    if (previousButton) previousButton.disabled = !isScrollable || rail.scrollLeft <= 2;
    if (nextButton) nextButton.disabled = !isScrollable || rail.scrollLeft >= maxScrollLeft - 2;
  },

  init(strip = document.querySelector('[data-ts-brand-series]')) {
    if (!strip) return;

    const rail = strip.querySelector('[data-ts-series-rail]');
    if (rail && rail.dataset.tsSeriesScrollBound !== 'true') {
      rail.dataset.tsSeriesScrollBound = 'true';
      rail.addEventListener(
        'scroll',
        () => {
          // The phone carousel uses native momentum scrolling and has no arrow
          // controls, so avoid layout reads while the finger is moving.
          if (this.mobileScrollMedia.matches || this.scrollFrame) return;

          this.scrollFrame = window.requestAnimationFrame(() => {
            this.scrollFrame = null;
            if (strip.isConnected) this.updateScrollControls(strip);
          });
        },
        { passive: true }
      );
    }

    this.updateScrollControls(strip);
    this.hydrateImages(strip);

    if (!this.resizeBound) {
      this.resizeBound = true;
      window.addEventListener('resize', () => {
        document.querySelectorAll('[data-ts-brand-series]').forEach((currentStrip) => {
          this.updateScrollControls(currentStrip);
        });
      });
    }
  },

  onClick(event) {
    const scrollButton = event.target.closest('[data-ts-series-scroll]');
    if (scrollButton) {
      const strip = scrollButton.closest('[data-ts-brand-series]');
      const rail = strip?.querySelector('[data-ts-series-rail]');
      if (!rail) return;

      const direction = scrollButton.dataset.tsSeriesScroll === 'previous' ? -1 : 1;
      rail.scrollBy({
        left: direction * Math.max(260, rail.clientWidth * 0.72),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
      return;
    }

    const link = event.target.closest('[data-ts-series-link]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof FacetFiltersForm === 'undefined' || !document.getElementById('ProductGridContainer')) return;

    event.preventDefault();
    const nextUrl = new URL(link.href, window.location.origin);
    const filterParameter = link.dataset.seriesParam;
    nextUrl.searchParams.delete(filterParameter);
    nextUrl.searchParams.delete('page');
    if (!link.classList.contains('is-active')) {
      nextUrl.searchParams.append(filterParameter, link.dataset.seriesValue);
    }

    const strip = link.closest('[data-ts-brand-series]');
    if (strip) {
      strip.setAttribute('aria-busy', 'true');
      strip.classList.add('is-loading');
    }

    FacetFiltersForm.renderPage(nextUrl.searchParams.toString());
  },
};

document.addEventListener('click', TimeShopBrandSeries.onClick.bind(TimeShopBrandSeries));
document.addEventListener('timeshop:product-grid-rendered', () => TimeShopBrandSeries.init());
TimeShopBrandSeries.init();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

window.TimeShopCollectionPagination = (() => {
  const rawPages = new Map();
  const readyPages = new Map();
  const parser = new DOMParser();
  const cardSelector = '#product-grid > [data-product-card-item]';
  const lifetime = 120000;
  let prefetchController;
  let prefetchTimer;

  function keyFor(source) {
    const url = new URL(source, window.location.origin);
    if (url.searchParams.get('page') === '1') url.searchParams.delete('page');
    url.searchParams.sort();
    return url.pathname + url.search;
  }

  function read(cache, key) {
    const entry = cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.time > lifetime) {
      cache.delete(key);
      return null;
    }
    cache.delete(key);
    cache.set(key, entry);
    return entry.html;
  }

  function remember(cache, key, html, limit) {
    cache.delete(key);
    cache.set(key, { html, time: Date.now() });
    while (cache.size > limit) cache.delete(cache.keys().next().value);
  }

  async function fetchSection(url, signal) {
    signal.throwIfAborted();
    const key = keyFor(url);
    const cached = read(rawPages, key);
    if (cached) return cached;
    const response = await fetch(url, { credentials: 'same-origin', signal });
    if (!response.ok) throw new Error('Collection response ' + response.status);
    const html = await response.text();
    signal.throwIfAborted();
    if (!html.includes('id="ProductGridContainer"')) throw new Error('Invalid collection section');
    remember(rawPages, key, html, 12);
    return html;
  }

  // Address just the required slices of the available/sold-out streams. This
  // keeps global availability ordering without downloading the entire catalog.
  function planSlices(page, size, available, total) {
    const start = (page - 1) * size;
    const end = Math.min(start + size, total);
    const slices = [];
    for (const [value, base, count] of [['1', 0, available], ['0', available, total - available]]) {
      let offset = Math.max(start, base) - base;
      const stop = Math.min(end, base + count) - base;
      while (offset < stop) {
        const sourcePage = Math.floor(offset / size) + 1;
        const from = offset % size;
        const length = Math.min(size - from, stop - offset);
        slices.push({ value, page: sourcePage, from, length });
        offset += length;
      }
    }
    return slices;
  }

  async function load(source, signal, initialHtml) {
    signal.throwIfAborted();
    const url = new URL(source, window.location.origin);
    const key = keyFor(url);
    const cached = read(readyPages, key);
    if (cached) return cached;
    const html = initialHtml || await fetchSection(url, signal);
    const doc = parser.parseFromString(html, 'text/html');
    const grid = doc.querySelector('ul#product-grid');
    if (grid) {
      const size = Number(grid.dataset.tsProductsPerPage);
      const total = Number(grid.dataset.tsProductCount);
      const available = Number(grid.dataset.tsAvailableCount);
      const soldOut = Number(grid.dataset.tsSoldOutCount);
      const page = Number(grid.dataset.tsCurrentPage);
      const selected = [...new Set(url.searchParams.getAll('filter.v.availability'))];
      const mixed = selected.length !== 1 && available > 0 && soldOut > 0
        && available + soldOut === total && total > size;
      if (mixed) {
        const slices = planSlices(page, size, available, total);
        const groups = await Promise.all(slices.map(async (slice) => {
          const partitionUrl = new URL(url);
          partitionUrl.searchParams.set('filter.v.availability', slice.value);
          partitionUrl.searchParams.set('page', slice.page);
          const partition = parser.parseFromString(await fetchSection(partitionUrl, signal), 'text/html');
          const cards = [...partition.querySelectorAll(cardSelector)].slice(slice.from, slice.from + slice.length);
          if (cards.length !== slice.length || cards.some((card) =>
            card.dataset.productAvailable !== (slice.value === '1' ? 'true' : 'false'))) {
            throw new Error('Availability changed while loading collection');
          }
          return cards.map((card) => card.outerHTML).join('');
        }));
        signal.throwIfAborted();
        grid.innerHTML = groups.join('');
      }
      grid.querySelectorAll('.ts-injected-promo-grid-item').forEach((item) => item.remove());
      delete grid.dataset.tsPromoInjectionKey;
      delete grid.dataset.tsPromoInjectedFor;
      grid.querySelectorAll('.scroll-trigger, .animate--slide-in').forEach((item) => {
        item.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
        item.classList.add('scroll-trigger--cancel');
        item.removeAttribute('data-cascade');
        item.style.removeProperty('--animation-order');
      });
    }
    const result = doc.body.innerHTML;
    remember(readyPages, key, result, 8);
    return result;
  }

  function cancelPrefetch() {
    clearTimeout(prefetchTimer);
    prefetchController?.abort();
    prefetchController = null;
  }

  function prefetchNext() {
    cancelPrefetch();
    if (navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
    const grid = document.querySelector('ul#product-grid');
    const nextPage = Number(grid?.dataset.tsCurrentPage) + 1;
    const totalPages = Number(grid?.dataset.tsTotalPages);
    if (!grid || !Number.isFinite(nextPage) || !Number.isFinite(totalPages) || nextPage > totalPages) return;
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', grid.dataset.id);
    url.searchParams.set('page', nextPage);
    if (read(readyPages, keyFor(url))) return;
    // Only one neighbouring page, once per navigation; never recursively crawl.
    prefetchTimer = setTimeout(() => {
      if (document.hidden) return;
      const controller = new AbortController();
      prefetchController = controller;
      const timeout = setTimeout(() => controller.abort(), 8000);
      load(url, controller.signal).catch(() => {}).finally(() => clearTimeout(timeout));
    }, 250);
  }

  let pendingScroll = null;

  function cancelScroll() {
    pendingScroll?.();
    pendingScroll = null;
  }

  function scrollToResultsStart(signal) {
    cancelScroll();
    if (signal?.aborted || document.hidden) return;
    let frame;
    let started;
    let from;
    let duration;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const events = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    const stop = () => {
      window.cancelAnimationFrame(frame);
      events.forEach(type => window.removeEventListener(type, stop, true));
      signal?.removeEventListener('abort', stop);
      document.removeEventListener('visibilitychange', onVisibility);
      if (pendingScroll === stop) pendingScroll = null;
    };
    const onVisibility = () => { if (document.hidden) stop(); };
    const align = timestamp => {
      if (signal?.aborted || document.hidden) { stop(); return; }
      const target = ['main-collection-filters', 'main-search-filters', 'ProductGridContainer']
        .map(id => document.getElementById(id))
        .find(element => element && element.getClientRects().length);
      if (!target || window.tsOverlayViewport?.isScrollLocked()) { stop(); return; }
      const header = document.querySelector('.shopify-section-header-sticky, sticky-header, .section-header');
      const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - (header?.getBoundingClientRect().height || 0) - 12);
      if (started === undefined) {
        started = timestamp;
        from = window.scrollY;
        duration = reducedMotion ? 0 : Math.min(760, Math.max(480, Math.abs(from - top) / 9));
      }
      const elapsed = timestamp - started;
      const progress = duration === 0 ? 1 : Math.min(1, elapsed / duration);
      const eased = progress * progress * (3 - 2 * progress);
      const position = from + (top - from) * eased;
      if (Math.abs(window.scrollY - position) > 0.5) window.scrollTo({ top: position, behavior: 'instant' });
      if (elapsed < duration + 200) frame = window.requestAnimationFrame(align);
      else stop();
    };
    pendingScroll = stop;
    events.forEach(type => window.addEventListener(type, stop, { capture: true, passive: true }));
    signal?.addEventListener('abort', stop, { once: true });
    document.addEventListener('visibilitychange', onVisibility);
    frame = window.requestAnimationFrame(align);
  }


  async function mount() {
    const grid = document.querySelector('ul#product-grid');
    const section = grid?.closest('.shopify-section');
    if (!section || FacetFiltersForm.controller) return;
    const controller = new AbortController();
    FacetFiltersForm.controller = controller;
    const timeout = setTimeout(() => controller.abort(), 10000);
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', grid.dataset.id);
    try {
      const html = await load(url, controller.signal, section.outerHTML);
      if (controller.signal.aborted || FacetFiltersForm.controller !== controller) return;
      // Replace initial cards only when cross-page availability ordering is needed.
      if (Number(grid.dataset.tsAvailableCount) > 0 && Number(grid.dataset.tsSoldOutCount) > 0) {
        FacetFiltersForm.renderProductGridContainer(html);
        document.dispatchEvent(new CustomEvent('timeshop:collection-grid-updated'));
      }
      prefetchNext();
    } catch (error) {
      if (!controller.signal.aborted) console.warn('TimeShop collection ordering skipped:', error);
    } finally {
      clearTimeout(timeout);
    }
  }

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('#ProductGridContainer .pagination-wrapper a[href]');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;
    event.preventDefault();
    FacetFiltersForm.renderPage(url.search.slice(1), null, true, true).then((rendered) => {
      if (rendered && event.detail === 0) {
        const current = document.querySelector('.pagination [aria-current="page"]');
        current?.setAttribute('tabindex', '-1');
        current?.focus({ preventScroll: true });
      }
    });
  });
  window.addEventListener('pagehide', () => {
    cancelScroll();
    cancelPrefetch();
    FacetFiltersForm.controller?.abort();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else queueMicrotask(mount);
  return { load, cancelPrefetch, prefetchNext, scrollToResultsStart, cancelScroll };
})();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
